import React from 'react';
import {
  Avatar,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  InputBase,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import TuneRoundedIcon from '@mui/icons-material/TuneRounded';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import { getCustomRecipes } from '@/lib/api/mealPlans';
import { RECIPE_DIET_LABEL_OPTIONS } from '@/constants/dietary-goals';
import { RecipeAllergenCard } from '@/components/recipes/RecipeAllergenCard';
import { formatDisplayLabel } from '@/utils/display-labels';
import { formatMacro } from '@/utils/gram-ml';
import { Tag } from '@/components/ui/tag';

export type RecipeLibraryItem = {
  id: string;
  title: string;
  description: string;
  kcal: number;
  mealType?: string;
  condition?: string;
  tags?: string[];
  allergens?: string[];
  protein?: number;
  carbs?: number;
  fat?: number;
  imageUrl?: string;
  provider?: 'spoonacular' | 'edamam' | 'internal';
  source?: 'api' | 'cached';
  nutritionServings?: number;
};

type ImportFromRecipeLibraryModalProps = {
  open: boolean;
  onClose: () => void;
  mealName?: string | null;
  /** When omitted, recipes load from GET /recipes/custom with `q` search + paginated lazy loading via the app API client. */
  recipes?: RecipeLibraryItem[];
  onImport: (selectedRecipes: RecipeLibraryItem[]) => void | Promise<void>;
  /** Recipe IDs already present in the target meal; hidden from import list. */
  hiddenRecipeIds?: string[];
  /** Client allergy tags to exclude from recipe library API search. */
  excludedAllergies?: string[];
  title?: string;
  subtitlePrefix?: string;
};

/** Page size per request (API max 100). Smaller chunks load faster and enable smooth infinite scroll. */
const CUSTOM_RECIPES_PAGE_SIZE = 25;
const SCROLL_LOAD_THRESHOLD_PX = 180;
const SEARCH_DEBOUNCE_MS = 400;

/** Meal filter labels shown in the modal; `All Meals` loads without a `mealType` query param. */
const MEAL_FILTER_OPTIONS = [
  'All Meals',
  'Breakfast',
  'Brunch',
  'Lunch',
  'Dinner',
  'Snack',
  'Tea Time',
] as const;

const mealTypeForApiRequest = (mealFilter: string): string | undefined => {
  const t = mealFilter.trim();
  if (!t || t === 'All Meals') return undefined;
  return t;
};

/** Case-insensitive match for static `recipes` prop (recipe `mealType` shapes vary). */
const recipeMealMatchesClientFilter = (recipeMeal: string | undefined, filter: string): boolean => {
  if (filter === 'All Meals') return true;
  const r = String(recipeMeal ?? '').trim().toLowerCase();
  const f = filter.trim().toLowerCase();
  if (!r) return false;
  if (r === f) return true;
  if (f === 'snack' && r.includes('snack')) return true;
  if (f === 'tea time' && (r.includes('tea') || r === 'teatime' || r.includes('tea time'))) return true;
  return r.includes(f) || f.includes(r);
};

const stringList = (value: unknown): string[] => {
  if (!Array.isArray(value)) return [];
  return value
    .map((item) => {
      if (typeof item === 'string') return item.trim();
      if (item && typeof item === 'object') {
        const o = item as Record<string, unknown>;
        const v = o.value ?? o.id ?? o.name;
        return v != null ? String(v).trim() : '';
      }
      return '';
    })
    .filter(Boolean);
};

const mapApiRecipeToLibraryItem = (raw: Record<string, unknown>): RecipeLibraryItem | null => {
  const id = String(raw.id ?? raw._id ?? '').trim();
  if (!id) return null;
  const title = String(raw.title ?? raw.name ?? raw.recipeName ?? 'Recipe').trim() || 'Recipe';
  const description = String(raw.description ?? '').trim();
  const nutritionPerServing =
    raw.nutritionPerServing && typeof raw.nutritionPerServing === 'object'
      ? (raw.nutritionPerServing as Record<string, unknown>)
      : null;
  const kcalRaw = Number(
    nutritionPerServing?.calories ?? raw.caloriesPerServing ?? raw.totalCalories ?? raw.calories ?? 0,
  );
  const kcal = Number.isFinite(kcalRaw) ? Math.max(0, kcalRaw) : 0;
  const protein = Number(raw.totalProtein ?? raw.protein ?? 0);
  const carbs = Number(raw.totalCarbs ?? raw.carbs ?? 0);
  const fat = Number(raw.totalFat ?? raw.fat ?? 0);
  const mealTypeRaw = raw.mealType;
  const mealType =
    Array.isArray(mealTypeRaw) && mealTypeRaw.length
      ? String(mealTypeRaw[0])
      : typeof mealTypeRaw === 'string'
        ? mealTypeRaw
        : undefined;
  const dishRaw = raw.dishType;
  const condition =
    Array.isArray(dishRaw) && dishRaw.length
      ? String(dishRaw[0])
      : typeof dishRaw === 'string'
        ? dishRaw
        : undefined;
  const tags = [...new Set([...stringList(raw.dietLabels), ...stringList(raw.healthLabels)])];
  const allergens = [...new Set([
    ...stringList(raw.allergens),
    ...stringList(raw.allergyTags),
    ...stringList(raw.cautions),
  ])];
  const imageRaw = raw.image ?? raw.imageUrl ?? raw.recipeImageUrl;
  const imageUrl = typeof imageRaw === 'string' && imageRaw.trim() ? imageRaw.trim() : undefined;
  return {
    id,
    title,
    description,
    kcal,
    mealType,
    condition,
    tags: tags.length ? tags : undefined,
    allergens: allergens.length ? allergens : undefined,
    protein: Number.isFinite(protein) ? protein : 0,
    carbs: Number.isFinite(carbs) ? carbs : 0,
    fat: Number.isFinite(fat) ? fat : 0,
    imageUrl,
    provider:
      raw.provider === 'edamam' || raw.provider === 'internal' || raw.provider === 'spoonacular'
        ? raw.provider
        : 'spoonacular',
    source: raw.source === 'cached' ? 'cached' : 'api',
    nutritionServings:
      Number.isFinite(Number(raw.nutritionServings)) && Number(raw.nutritionServings) > 0
        ? Number(raw.nutritionServings)
        : 1,
  };
};

/** Normalize diet tag strings from API (underscores / hyphens / case). */
const normalizeDietToken = (s: string): string => s.trim().toLowerCase().replace(/-/g, '_');

const tagMatchesDietValue = (tag: string, dietValue: string): boolean =>
  normalizeDietToken(tag) === normalizeDietToken(dietValue);

const getRecipeTagDisplayLabel = (tag: string): string => {
  const normalizedTag = normalizeDietToken(tag);
  return (
    RECIPE_DIET_LABEL_OPTIONS.find((option) => normalizeDietToken(option.value) === normalizedTag)?.name ??
    formatDisplayLabel(tag)
  );
};

/** Comma-separated `diet` query (see `GetCustomRecipesParams`). */
const dietLabelsForApiRequest = (values: string[]): string | undefined => {
  const unique = [...new Set(values.map((v) => v.trim()).filter(Boolean))];
  if (!unique.length) return undefined;
  return unique.join(',');
};

/** Client-side: recipe must include every selected label (AND). */
const recipeTagsIncludeAllDietLabels = (tags: string[] | undefined, filters: string[]): boolean => {
  if (!filters.length) return true;
  return filters.every((f) => tags?.some((tag) => tagMatchesDietValue(tag, f)));
};

export const ImportFromRecipeLibraryModal: React.FC<ImportFromRecipeLibraryModalProps> = ({
  open,
  onClose,
  mealName,
  recipes: recipesProp,
  onImport,
  hiddenRecipeIds = [],
  excludedAllergies = [],
  title = 'Import from Recipe Library',
  subtitlePrefix = 'Adding to:',
}) => {
  const [searchValue, setSearchValue] = React.useState('');
  /** Trimmed query used for GET /recipes/custom `q` (debounced when loading from API). */
  const [debouncedSearchQuery, setDebouncedSearchQuery] = React.useState('');
  const [mealFilter, setMealFilter] = React.useState('All Meals');
  /** API diet values (`RECIPE_DIET_LABEL_OPTIONS`), sent comma-separated; empty = no filter. */
  const [selectedDietLabels, setSelectedDietLabels] = React.useState<string[]>([]);
  const [selectedRecipeIds, setSelectedRecipeIds] = React.useState<string[]>([]);
  const [expandedRecipeId, setExpandedRecipeId] = React.useState<string | null>(null);
  const [isImporting, setIsImporting] = React.useState(false);
  const [fetchedRecipes, setFetchedRecipes] = React.useState<RecipeLibraryItem[]>([]);
  const [initialLoading, setInitialLoading] = React.useState(false);
  const [loadingMore, setLoadingMore] = React.useState(false);
  const [listError, setListError] = React.useState<string | null>(null);
  const [fetchKey, setFetchKey] = React.useState(0);
  const [nextPage, setNextPage] = React.useState(1);
  const [hasMore, setHasMore] = React.useState(true);
  const [totalCount, setTotalCount] = React.useState(0);
  const listScrollRef = React.useRef<HTMLDivElement>(null);
  const loadMoreInFlightRef = React.useRef(false);
  const nextPageRef = React.useRef(1);
  const hasMoreRef = React.useRef(true);
  /** Same `q` as the current list request chain (page 1 + lazy pages). */
  const activeListQueryRef = React.useRef('');
  /** Same `mealType` as the current list request chain (empty = all meals). */
  const activeMealTypeRef = React.useRef('');
  /** Same `diet` query as the current list request chain (empty = no diet filter). */
  const activeDietLabelRef = React.useRef('');
  /** Same allergy exclusion query as the current list request chain (empty = no client allergy exclusions). */
  const activeAllergyRef = React.useRef('');
  /** Incremented when a new list query starts so stale pagination responses are ignored. */
  const listRequestGenerationRef = React.useRef(0);

  React.useEffect(() => {
    nextPageRef.current = nextPage;
  }, [nextPage]);

  React.useEffect(() => {
    hasMoreRef.current = hasMore;
  }, [hasMore]);

  const recipes = recipesProp ?? fetchedRecipes;
  const hiddenRecipeIdSet = React.useMemo(
    () => new Set(hiddenRecipeIds.map((id) => String(id).trim()).filter(Boolean)),
    [hiddenRecipeIds],
  );
  const excludedAllergyParam = React.useMemo(
    () => Array.from(new Set(
      excludedAllergies
        .map((value) => String(value ?? '').trim())
        .filter(Boolean),
    )).join(','),
    [excludedAllergies],
  );

  React.useEffect(() => {
    if (!hiddenRecipeIdSet.size) return;
    setSelectedRecipeIds((prev) => prev.filter((id) => !hiddenRecipeIdSet.has(String(id).trim())));
  }, [hiddenRecipeIdSet]);

  React.useEffect(() => {
    if (!open) return;
    setSearchValue('');
    setDebouncedSearchQuery('');
    setMealFilter('All Meals');
    setSelectedDietLabels([]);
    setSelectedRecipeIds([]);
    setExpandedRecipeId(null);
  }, [open, mealName]);

  React.useEffect(() => {
    if (!open || recipesProp !== undefined) return;
    const handle = window.setTimeout(() => {
      setDebouncedSearchQuery(searchValue.trim());
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(handle);
  }, [open, recipesProp, searchValue]);

  React.useEffect(() => {
    if (!open || recipesProp !== undefined) return;
    const el = listScrollRef.current;
    if (el) el.scrollTop = 0;
  }, [debouncedSearchQuery, selectedDietLabels, mealFilter, open, recipesProp]);

  React.useEffect(() => {
    if (!open || recipesProp !== undefined) return;

    let cancelled = false;
    listRequestGenerationRef.current += 1;
    const requestGeneration = listRequestGenerationRef.current;
    setInitialLoading(true);
    setLoadingMore(false);
    setListError(null);
    setFetchedRecipes([]);
    setNextPage(1);
    setHasMore(true);
    setTotalCount(0);
    loadMoreInFlightRef.current = false;
    nextPageRef.current = 1;
    hasMoreRef.current = true;
    activeListQueryRef.current = debouncedSearchQuery;
    activeMealTypeRef.current = mealTypeForApiRequest(mealFilter) ?? '';
    activeDietLabelRef.current = dietLabelsForApiRequest(selectedDietLabels) ?? '';
    activeAllergyRef.current = excludedAllergyParam;

    const qParam = debouncedSearchQuery.trim() || undefined;
    const mealTypeParam = mealTypeForApiRequest(mealFilter);
    const dietParam = dietLabelsForApiRequest(selectedDietLabels);
    const allergyParam = excludedAllergyParam || undefined;

    void (async () => {
      try {
        const response = await getCustomRecipes({
          page: 1,
          pageSize: CUSTOM_RECIPES_PAGE_SIZE,
          ...(qParam ? { q: qParam } : {}),
          ...(mealTypeParam ? { mealType: mealTypeParam } : {}),
          ...(dietParam ? { diet: dietParam } : {}),
          ...(allergyParam ? { allergens: allergyParam } : {}),
        });
        if (cancelled || listRequestGenerationRef.current !== requestGeneration) return;
        if (!response.success) {
          setListError(response.message || response.error || 'Could not load recipes');
          setFetchedRecipes([]);
          setHasMore(false);
          hasMoreRef.current = false;
          return;
        }
        const mapped = (response.data ?? [])
          .map((item) => mapApiRecipeToLibraryItem(item as unknown as Record<string, unknown>))
          .filter((item): item is RecipeLibraryItem => item !== null);
        setFetchedRecipes(mapped);
        const pagination = (response.pagination ?? {}) as Record<string, unknown>;
        const totalPagesRaw = Number(pagination.totalPages);
        const totalPages = Number.isFinite(totalPagesRaw) && totalPagesRaw >= 1 ? totalPagesRaw : 1;
        const total = Number(pagination.totalCount ?? pagination.total ?? 0);
        setTotalCount(Number.isFinite(total) && total > 0 ? total : mapped.length);
        let initialMore = false;
        if (mapped.length === 0) initialMore = false;
        else if (mapped.length < CUSTOM_RECIPES_PAGE_SIZE) initialMore = false;
        else if (totalPages > 1) initialMore = 1 < totalPages;
        else initialMore = true;
        setHasMore(initialMore);
        hasMoreRef.current = initialMore;
        setNextPage(2);
        nextPageRef.current = 2;
      } catch (e) {
        if (!cancelled && listRequestGenerationRef.current === requestGeneration) {
          setListError(e instanceof Error ? e.message : 'Could not load recipes');
          setFetchedRecipes([]);
          setHasMore(false);
          hasMoreRef.current = false;
        }
      } finally {
        if (!cancelled && listRequestGenerationRef.current === requestGeneration) setInitialLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, recipesProp, fetchKey, debouncedSearchQuery, selectedDietLabels, mealFilter, excludedAllergyParam]);

  const loadNextPage = React.useCallback(async () => {
    if (recipesProp !== undefined || !hasMoreRef.current || loadMoreInFlightRef.current || initialLoading) return;
    const pageToLoad = nextPageRef.current;
    if (pageToLoad < 2) return;

    const generationAtStart = listRequestGenerationRef.current;
    loadMoreInFlightRef.current = true;
    setLoadingMore(true);
    const qNext = activeListQueryRef.current.trim() || undefined;
    const mealTypeNext = activeMealTypeRef.current.trim() || undefined;
    const dietNext = activeDietLabelRef.current.trim() || undefined;
    const allergyNext = activeAllergyRef.current.trim() || undefined;
    try {
      const response = await getCustomRecipes({
        page: pageToLoad,
        pageSize: CUSTOM_RECIPES_PAGE_SIZE,
        ...(qNext ? { q: qNext } : {}),
        ...(mealTypeNext ? { mealType: mealTypeNext } : {}),
        ...(dietNext ? { diet: dietNext } : {}),
        ...(allergyNext ? { allergens: allergyNext } : {}),
      });
      if (listRequestGenerationRef.current !== generationAtStart) return;
      if (!response.success) {
        setHasMore(false);
        hasMoreRef.current = false;
        return;
      }
      const mapped = (response.data ?? [])
        .map((item) => mapApiRecipeToLibraryItem(item as unknown as Record<string, unknown>))
        .filter((item): item is RecipeLibraryItem => item !== null);
      if (listRequestGenerationRef.current !== generationAtStart) return;
      if (mapped.length === 0) {
        setHasMore(false);
        hasMoreRef.current = false;
        return;
      }
      setFetchedRecipes((prev) => {
        const seen = new Set(prev.map((r) => r.id));
        const merged = [...prev];
        for (const item of mapped) {
          if (!seen.has(item.id)) {
            merged.push(item);
            seen.add(item.id);
          }
        }
        return merged;
      });
      const pagination = (response.pagination ?? {}) as Record<string, unknown>;
      const totalPagesRaw = Number(pagination.totalPages);
      const totalPages = Number.isFinite(totalPagesRaw) && totalPagesRaw >= 1 ? totalPagesRaw : 1;
      let more = false;
      if (mapped.length < CUSTOM_RECIPES_PAGE_SIZE) more = false;
      else if (totalPages > 1) more = pageToLoad < totalPages;
      else more = true;
      setHasMore(more);
      hasMoreRef.current = more;
      const next = pageToLoad + 1;
      nextPageRef.current = next;
      setNextPage(next);
    } catch {
      if (listRequestGenerationRef.current === generationAtStart) {
        setHasMore(false);
        hasMoreRef.current = false;
      }
    } finally {
      if (listRequestGenerationRef.current === generationAtStart) {
        loadMoreInFlightRef.current = false;
        setLoadingMore(false);
      }
    }
  }, [initialLoading, recipesProp]);

  const loadingMoreRef = React.useRef(false);
  React.useEffect(() => {
    loadingMoreRef.current = loadingMore;
  }, [loadingMore]);

  const handleRecipeListScroll = React.useCallback(() => {
    const el = listScrollRef.current;
    if (!el || listError || initialLoading || !hasMoreRef.current || loadingMoreRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = el;
    if (scrollHeight - scrollTop - clientHeight < SCROLL_LOAD_THRESHOLD_PX) {
      void loadNextPage();
    }
  }, [initialLoading, listError, loadNextPage]);

  const filteredRecipes = React.useMemo(() => {
    const normalizedSearch = searchValue.trim().toLowerCase();
    return recipes.filter((recipe) => {
      const isAlreadyAdded = hiddenRecipeIdSet.has(String(recipe.id).trim());
      if (isAlreadyAdded) return false;
      const matchesSearch =
        recipesProp === undefined
          ? true
          : !normalizedSearch ||
            recipe.title.toLowerCase().includes(normalizedSearch) ||
            recipe.description.toLowerCase().includes(normalizedSearch);
      const matchesMeal =
        recipesProp === undefined
          ? true
          : recipeMealMatchesClientFilter(recipe.mealType, mealFilter);
      const matchesDietLabels =
        recipesProp === undefined
          ? true
          : recipeTagsIncludeAllDietLabels(recipe.tags, selectedDietLabels);
      return matchesSearch && matchesMeal && matchesDietLabels;
    });
  }, [recipes, recipesProp, searchValue, mealFilter, selectedDietLabels, hiddenRecipeIdSet]);

  const selectedRecipes = React.useMemo(
    () => recipes.filter((recipe) => selectedRecipeIds.includes(recipe.id)),
    [recipes, selectedRecipeIds],
  );
  const selectedCount = selectedRecipeIds.length;

  const toggleRecipeSelection = React.useCallback((recipeId: string) => {
    setSelectedRecipeIds((prev) =>
      prev.includes(recipeId)
        ? prev.filter((id) => id !== recipeId)
        : [...prev, recipeId],
    );
  }, []);

  const toggleRecipeDetails = React.useCallback((recipeId: string) => {
    setExpandedRecipeId((prev) => (prev === recipeId ? null : recipeId));
  }, []);

  const handleImport = React.useCallback(async () => {
    if (!selectedRecipes.length) return;
    try {
      setIsImporting(true);
      await onImport(selectedRecipes);
      onClose();
    } catch {
      // Parent handles toast/errors; keep modal open for retry.
    } finally {
      setIsImporting(false);
    }
  }, [selectedRecipes, onImport, onClose]);

  const apiSearchPending =
    recipesProp === undefined && searchValue.trim() !== debouncedSearchQuery;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth={false}
      PaperProps={{
        sx: {
          width: { xs: 'calc(100vw - 2rem)', sm: '700px' },
          /** Match Figma modal footprint while staying within viewport. */
          height: { xs: 'calc(100vh - 3rem)', sm: 'min(897.5px, calc(100vh - 4rem))' },
          maxHeight: { xs: 'calc(100vh - 3rem)', sm: 'min(897.5px, calc(100vh - 4rem))' },
          borderRadius: '16px',
          boxShadow: '0px 25px 50px -12px rgba(0,0,0,0.25)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        },
      }}
    >
      <DialogContent
        sx={{
          p: 0,
          overflow: 'hidden',
          borderRadius: '16px',
          display: 'flex',
          flexDirection: 'column',
          flex: '1 1 auto',
          minHeight: 0,
        }}
      >
        <Box
          sx={{
            px: '24px',
            py: '20px',
            borderBottom: '1px solid #F3F4F6',
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: '12px',
            flexShrink: 0,
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minHeight: '42.5px' }}>
            <Box
              sx={{
                width: '40px',
                height: '40px',
                borderRadius: '14px',
                bgcolor: '#FFF7ED',
                display: 'grid',
                placeItems: 'center',
                flexShrink: 0,
              }}
            >
              <DownloadRoundedIcon sx={{ fontSize: 18, color: '#F97316' }} />
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
              <Typography
                sx={{
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '18px',
                  fontWeight: 700,
                  lineHeight: '22.5px',
                  letterSpacing: '-0.4395px',
                  color: '#1E2939',
                }}
              >
                {title}
              </Typography>
              <Typography
                sx={{
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '12px',
                  fontWeight: 400,
                  lineHeight: '18px',
                  color: '#1EA9D6',
                }}
              >
                {subtitlePrefix} {mealName || 'Meal'}
              </Typography>
            </Box>
          </Stack>

          <IconButton
            onClick={onClose}
            sx={{
              width: '32px',
              height: '32px',
              border: '1px solid #E5E7EB',
              borderRadius: '999px',
              color: '#9CA3AF',
              p: '1px',
              alignSelf: 'flex-start',
            }}
          >
            <CloseRoundedIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Box>

        <Box
          sx={{
            px: '24px',
            py: '12px',
            display: 'flex',
            flexDirection: 'column',
            flex: '1 1 auto',
            minHeight: 0,
            overflow: 'hidden',
            '& .MuiOutlinedInput-root': {
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: '#E5E7EB',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: '#E5E7EB',
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: '#E5E7EB',
                borderWidth: 1,
              },
            },
          }}
        >
          <Stack direction="row" spacing={1.5} sx={{ flexWrap: 'wrap', flexShrink: 0, minHeight: '65.5px', alignItems: 'center' }}>
            <Box
              sx={{
                px: '13px',
                height: '37.5px',
                borderRadius: '14px',
                border: '1px solid #E5E7EB',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                minWidth: 300,
                flex: 1,
                bgcolor: '#fff',
              }}
            >
              <SearchRoundedIcon sx={{ fontSize: 14, color: '#99A1AF' }} />
              <InputBase
                value={searchValue}
                onChange={(event) => setSearchValue(event.target.value)}
                placeholder="Search recipes..."
                sx={{
                  width: '100%',
                  '& .MuiInputBase-input': {
                    p: 0,
                    fontSize: '13px',
                    letterSpacing: '-0.0762px',
                    color: '#374151',
                  },
                }}
              />
            </Box>

            <Select
              value={mealFilter}
              onChange={(event) => setMealFilter(event.target.value)}
              size="small"
              IconComponent={KeyboardArrowDownRoundedIcon}
              sx={{
                minWidth: '100.688px',
                height: '37.5px',
                borderRadius: '14px',
                fontSize: '13px',
                color: '#374151',
                '& .MuiOutlinedInput-notchedOutline': { borderColor: '#E5E7EB' },
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#E5E7EB' },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#E5E7EB', borderWidth: 1 },
                '& .MuiSelect-select': { py: '9px', pr: '26px', fontWeight: 500, lineHeight: '19.5px', letterSpacing: '-0.0762px' },
                '& .MuiSelect-icon': { fontSize: '13px', color: '#9CA3AF', right: '11px' },
              }}
            >
              {MEAL_FILTER_OPTIONS.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>

            <Select
              multiple
              displayEmpty
              value={selectedDietLabels}
              onChange={(event) => {
                const v = event.target.value;
                setSelectedDietLabels(typeof v === 'string' ? v.split(',') : v);
              }}
              size="small"
              IconComponent={KeyboardArrowDownRoundedIcon}
              renderValue={(selected) => {
                const arr = selected as string[];
                if (!arr.length) return 'All Conditions';
                return `${arr.length} label${arr.length === 1 ? '' : 's'}`;
              }}
              sx={{
                minWidth: '130.867px',
                maxWidth: 280,
                height: '37.5px',
                borderRadius: '14px',
                fontSize: '13px',
                color: '#374151',
                '& .MuiOutlinedInput-notchedOutline': { borderColor: '#E5E7EB' },
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#E5E7EB' },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#E5E7EB', borderWidth: 1 },
                '& .MuiSelect-select': { py: '9px', pr: '26px', fontWeight: 500, lineHeight: '19.5px', letterSpacing: '-0.0762px' },
                '& .MuiSelect-icon': { fontSize: '13px', color: '#9CA3AF', right: '11px' },
              }}
              MenuProps={{ PaperProps: { sx: { maxHeight: 360 } } }}
            >
              {RECIPE_DIET_LABEL_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  <Checkbox
                    checked={selectedDietLabels.includes(option.value)}
                    size="small"
                    sx={{ py: 0, mr: 0.75, color: '#D0D5DD', '&.Mui-checked': { color: '#20AEDC' } }}
                  />
                  {option.name}
                </MenuItem>
              ))}
            </Select>
          </Stack>

          {selectedDietLabels.length > 0 ? (
            <Box
              sx={{
                mt: 0.25,
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                gap: 0.75,
                flexShrink: 0,
              }}
            >
              <TuneRoundedIcon sx={{ fontSize: 13, color: '#9CA3AF', mr: 0.25 }} />
              {selectedDietLabels.map((value) => {
                const opt = RECIPE_DIET_LABEL_OPTIONS.find((o) => o.value === value);
                return (
                  <Tag
                    key={value}
                    variant="selected"
                    size="md"
                    className="h-7 bg-white font-[Inter,sans-serif] text-xs font-normal text-[#6B7280]"
                    onRemove={() => setSelectedDietLabels((prev) => prev.filter((v) => v !== value))}
                    removeAriaLabel={`Remove ${opt?.name ?? value}`}
                  >
                    {opt?.name ?? value}
                  </Tag>
                );
              })}
            </Box>
          ) : null}

          <Typography sx={{ mt: 1.25, flexShrink: 0, fontFamily: 'Inter, sans-serif', fontSize: '12px', lineHeight: '18px', color: '#9CA3AF' }}>
            {recipesProp !== undefined
              ? `${filteredRecipes.length} recipes found`
              : initialLoading
                ? 'Loading recipes…'
                : listError
                  ? 'Unable to load recipes'
                  : apiSearchPending
                    ? 'Updating search…'
                    : totalCount > recipes.length
                      ? `${filteredRecipes.length} shown · ${recipes.length} loaded of ${totalCount}`
                      : `${filteredRecipes.length} shown · ${recipes.length} loaded`}
          </Typography>

          <Box
            ref={listScrollRef}
            onScroll={handleRecipeListScroll}
            sx={{
              mt: 1,
              flex: '1 1 auto',
              minHeight: 0,
              overflowY: 'auto',
              overflowX: 'hidden',
              pr: '12px',
              borderBottom: '1px solid #F3F4F6',
              overscrollBehavior: 'contain',
              scrollbarGutter: 'stable',
              scrollbarWidth: 'thin',
              scrollbarColor: '#D1D5DB transparent',
              '&::-webkit-scrollbar': { width: 6, height: 6 },
              '&::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: '#D1D5DB',
                borderRadius: 999,
              },
            }}
          >
            {initialLoading ? (
              <Box sx={{ py: 6, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <CircularProgress size={36} sx={{ color: '#20AEDC' }} />
              </Box>
            ) : listError ? (
              <Box sx={{ py: 4, px: 2, textAlign: 'center' }}>
                <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.86rem', color: '#B42318', mb: 1.5 }}>
                  {listError}
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setFetchKey((k) => k + 1)}
                  sx={{ textTransform: 'none', borderRadius: '10px' }}
                >
                  Retry
                </Button>
              </Box>
            ) : (
              <>
            {filteredRecipes.map((recipe) => {
              const isSelected = selectedRecipeIds.includes(recipe.id);
              const isExpanded = expandedRecipeId === recipe.id;
              return (
                <Box key={recipe.id} sx={{ borderBottom: '1px solid #F3F4F6' }}>
                  <Box
                    sx={{
                      py: 0,
                      minHeight: '68px',
                      display: 'grid',
                      gridTemplateColumns: '20px 44px minmax(0,1fr) max-content max-content',
                      alignItems: 'center',
                      columnGap: '12px',
                      width: '100%',
                      minWidth: 0,
                    }}
                  >
                    <Checkbox
                      checked={isSelected}
                      onChange={() => toggleRecipeSelection(recipe.id)}
                      icon={
                        <Box
                          sx={{
                            width: '20px',
                            height: '20px',
                            borderRadius: '4px',
                            border: '2px solid #D1D5DB',
                            bgcolor: '#FFFFFF',
                            boxSizing: 'border-box',
                          }}
                        />
                      }
                      checkedIcon={
                        <Box
                          sx={{
                            width: '20px',
                            height: '20px',
                            borderRadius: '4px',
                            border: '2px solid #1EA9D6',
                            bgcolor: '#1EA9D6',
                            boxSizing: 'border-box',
                            display: 'grid',
                            placeItems: 'center',
                          }}
                        >
                          <CheckRoundedIcon sx={{ fontSize: 14, color: '#FFFFFF' }} />
                        </Box>
                      }
                      sx={{
                        p: 0,
                        m: 0,
                      }}
                    />

                    <Box
                      sx={{
                        width: 44,
                        height: 44,
                        borderRadius: '14px',
                        border: '1px solid #F3F4F6',
                        boxShadow: '0px 1px 3px 0px rgba(0,0,0,0.1), 0px 1px 2px -1px rgba(0,0,0,0.1)',
                        bgcolor: 'transparent',
                        overflow: 'hidden',
                        flexShrink: 0,
                      }}
                    >
                      {recipe.imageUrl ? (
                        <Box sx={{ position: 'relative', width: '42px', height: '42px', borderRadius: 'inherit', overflow: 'hidden' }}>
                          <Box
                            component="img"
                            src={recipe.imageUrl}
                            alt={recipe.title}
                            sx={{
                              position: 'absolute',
                              inset: 0,
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                              pointerEvents: 'none',
                            }}
                          />
                        </Box>
                      ) : (
                        <Avatar
                          variant="rounded"
                          sx={{
                            width: '42px',
                            height: '42px',
                            borderRadius: 'inherit',
                            bgcolor: '#F2F4F7',
                            fontSize: '0.82rem',
                            color: '#667085',
                          }}
                        >
                          {recipe.title.slice(0, 1)}
                        </Avatar>
                      )}
                    </Box>

                    <Box sx={{ minWidth: 0, pr: 1 }}>
                      <Typography
                        sx={{
                          fontFamily: 'Inter, sans-serif',
                          fontSize: '13px',
                          lineHeight: '16.25px',
                          letterSpacing: '-0.0762px',
                          fontWeight: 600,
                          color: '#1E2939',
                          m: 0,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {recipe.title}
                      </Typography>
                      <Typography
                        sx={{
                          fontFamily: 'Inter, sans-serif',
                          fontSize: '12px',
                          lineHeight: '16.5px',
                          letterSpacing: '0.0645px',
                          fontWeight: 400,
                          color: '#9CA3AF',
                          m: 0,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {recipe.description}
                      </Typography>
                      <RecipeAllergenCard
                        allergens={recipe.allergens}
                        compact
                        maxVisible={2}
                        className="mt-0.5 max-w-full"
                      />
                    </Box>

                    <Tag
                      variant="warning"
                      size="xs"
                      className="h-[20.5px] justify-self-end font-[Inter,sans-serif] text-xs font-semibold leading-[16.5px] text-[#F59E0B]"
                    >
                      {`${formatMacro(recipe.kcal)} kcal`}
                    </Tag>

                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => toggleRecipeDetails(recipe.id)}
                      sx={{
                        justifySelf: 'end',
                        minHeight: '28px',
                        minWidth: '66.938px',
                        borderRadius: '10px',
                        borderColor: '#1EA9D6',
                        color: isExpanded ? '#ffffff' : '#1EA9D6',
                        textTransform: 'none',
                        fontFamily: 'Inter, sans-serif',
                        fontSize: '12px',
                        lineHeight: '18px',
                        fontWeight: 600,
                        letterSpacing: 0,
                        px: 1.2,
                        bgcolor: isExpanded ? '#1EA9D6' : '#fff',
                        '&:hover': {
                          bgcolor: isExpanded ? '#1EA9D6' : '#fff',
                          borderColor: '#1EA9D6',
                        },
                      }}
                    >
                      Details
                    </Button>
                  </Box>

                  {isExpanded ? (
                    <Box
                      sx={{
                        mx: 1.9,
                        mb: 1.15,
                        pt: '17px',
                        pr: '17px',
                        pb: '17px',
                        pl: '17px',
                        borderRadius: '14px',
                        bgcolor: '#EEFBFF',
                        border: '1px solid #E0F7FB',
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'flex-start',
                        gap: '16px',
                      }}
                    >
                      <Box
                        sx={{
                          width: 80,
                          height: 80,
                          flexShrink: 0,
                          borderRadius: '14px',
                          border: '1px solid #A5F3FC',
                          overflow: 'hidden',
                          bgcolor: '#F2F4F7',
                        }}
                      >
                        {recipe.imageUrl ? (
                          <Box
                            component="img"
                            src={recipe.imageUrl}
                            alt=""
                            sx={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                              display: 'block',
                            }}
                          />
                        ) : (
                          <Avatar
                            variant="rounded"
                            sx={{
                              width: '100%',
                              height: '100%',
                              borderRadius: '14px',
                              fontSize: '1.1rem',
                              color: '#667085',
                            }}
                          >
                            {recipe.title.slice(0, 1)}
                          </Avatar>
                        )}
                      </Box>

                      <Box sx={{ minWidth: 0, flex: '1 1 auto' }}>
                        <Typography
                          sx={{
                            fontFamily: 'Inter, sans-serif',
                            fontSize: '12px',
                            fontWeight: 400,
                            lineHeight: '18px',
                            color: '#0E7490',
                            mb: '11px',
                          }}
                        >
                          {(recipe.description || recipe.title).trim() || recipe.title}
                        </Typography>

                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                            gap: '16px',
                            flexWrap: 'wrap',
                            minHeight: '38px',
                            width: '100%',
                          }}
                        >
                          <Box
                            sx={{
                              display: 'flex',
                              flexDirection: 'row',
                              alignItems: 'center',
                              gap: '16px',
                              flexWrap: 'wrap',
                              rowGap: '8px',
                            }}
                          >
                            {[
                              { label: 'Calories', value: `${formatMacro(recipe.kcal)} kcal` },
                              { label: 'Protein', value: `${formatMacro(recipe.protein ?? 0)}g` },
                              { label: 'Carbs', value: `${formatMacro(recipe.carbs ?? 0)}g` },
                              { label: 'Fat', value: `${formatMacro(recipe.fat ?? 0)}g` },
                            ].map((metric) => (
                              <Stack
                                key={`${recipe.id}-${metric.label}`}
                                spacing="2px"
                                sx={{ alignItems: 'flex-start' }}
                              >
                                <Typography
                                  sx={{
                                    fontFamily: 'Inter, sans-serif',
                                    fontSize: '12px',
                                    fontWeight: 400,
                                    lineHeight: '16.5px',
                                    letterSpacing: '0.0645px',
                                    color: '#9CA3AF',
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  {metric.label}
                                </Typography>
                                <Typography
                                  sx={{
                                    fontFamily: 'Inter, sans-serif',
                                    fontSize: '13px',
                                    fontWeight: 600,
                                    lineHeight: '19.5px',
                                    letterSpacing: '-0.0762px',
                                    color: '#0A0A0A',
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  {metric.value}
                                </Typography>
                              </Stack>
                            ))}
                          </Box>

                          {recipe.tags?.length ? (
                            <Stack
                              direction="row"
                              sx={{
                                flexWrap: 'wrap',
                                gap: '8px',
                                ml: { xs: 0, sm: 'auto' },
                                justifyContent: 'flex-end',
                              }}
                            >
                              {recipe.tags.map((tag) => (
                                <Tag
                                  key={`${recipe.id}-${tag}`}
                                  variant="outlined"
                                  size="sm"
                                  className="border-[#CFD9DD] bg-white px-[10px] py-[3px] font-[Inter,sans-serif] text-xs font-semibold leading-[15px] text-[#0E7490]"
                                >
                                  {getRecipeTagDisplayLabel(tag)}
                                </Tag>
                              ))}
                            </Stack>
                          ) : null}
                        </Box>
                      </Box>
                    </Box>
                  ) : null}
                </Box>
              );
            })}

            {loadingMore ? (
              <Box sx={{ py: 1.75, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <CircularProgress size={22} sx={{ color: '#20AEDC' }} />
              </Box>
            ) : null}

            {!filteredRecipes.length ? (
              <Typography sx={{ py: 2.4, textAlign: 'center', fontFamily: 'Inter, sans-serif', fontSize: '0.84rem', color: '#98A2B3' }}>
                {recipes.length ? 'No recipes match your filters' : 'No recipes found'}
              </Typography>
            ) : null}
              </>
            )}
          </Box>

          <Box
            sx={{
              minHeight: '70.5px',
              pt: '1px',
              px: 0,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: '8px',
              flexShrink: 0,
              mt: 'auto',
              borderTop: '1px solid #F3F4F6',
            }}
          >
            <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '12px', lineHeight: '18px', color: '#9CA3AF' }}>
              {selectedCount ? `${selectedCount} recipe${selectedCount === 1 ? '' : 's'} selected` : 'Select recipes to add them to your meal plan'}
            </Typography>
            <Stack direction="row" spacing={1} sx={{ width: '197.602px', height: '37.5px' }}>
              <Button
                variant="outlined"
                onClick={onClose}
                sx={{
                  textTransform: 'none',
                  borderRadius: '14px',
                  height: '37.5px',
                  width: '85.391px',
                  minWidth: '85.391px',
                  px: 2.2,
                  color: '#374151',
                  borderColor: '#E5E7EB',
                  fontSize: '13px',
                  fontWeight: 600,
                  lineHeight: '19.5px',
                  letterSpacing: '-0.0762px',
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={() => {
                  void handleImport();
                }}
                disabled={!selectedRecipeIds.length || isImporting}
                startIcon={<DownloadRoundedIcon sx={{ fontSize: 14, mr: 0.1 }} />}
                sx={{
                  textTransform: 'none',
                  borderRadius: '14px',
                  height: '35.5px',
                  minWidth: 0,
                  flex: 1,
                  px: 2.2,
                  bgcolor: selectedCount ? '#1D2939' : '#9CA3AF',
                  color: '#ffffff',
                  fontSize: '13px',
                  fontWeight: 600,
                  lineHeight: '19.5px',
                  letterSpacing: '-0.0762px',
                  '&.Mui-disabled': {
                    bgcolor: '#9CA3AF',
                    color: '#fff',
                  },
                  '&:hover': { bgcolor: selectedCount ? '#101828' : '#7f8ea4' },
                }}
              >
                {isImporting ? 'Importing...' : 'Import'}
              </Button>
            </Stack>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};
